import type {
  SlrLoanStandingAction,
  SlrLoanStandingAuditState,
  SlrLoanStandingPresentation,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

const ACTION_TO_CTA_KEY: Partial<Record<SlrLoanStandingAction, string>> = {
  upload_document: 'slr.loan_standing.actions.upload_document',
  upload_newer_document: 'slr.loan_standing.actions.upload_newer_document',
  confirm_name_match: 'slr.loan_standing.actions.confirm_name_match',
  contact_support: 'slr.loan_standing.actions.contact_support',
};

const STATUS_TO_TITLE_KEY: Record<SlrLoanStandingStatus, string> = {
  unknown: 'slr.loan_standing.title.pending_upload',
  pending_upload: 'slr.loan_standing.title.pending_upload',
  processing: 'slr.loan_standing.title.processing',
  eligible: 'slr.loan_standing.title.eligible',
  ineligible: 'slr.loan_standing.title.ineligible',
  try_again: 'slr.loan_standing.title.try_again',
  cannot_determine: 'slr.loan_standing.title.cannot_determine',
};

const STATUS_TO_DESCRIPTION_KEY: Record<SlrLoanStandingStatus, string> = {
  unknown: 'slr.loan_standing.description.pending_upload',
  pending_upload: 'slr.loan_standing.description.pending_upload',
  processing: 'slr.loan_standing.description.processing',
  eligible: 'slr.loan_standing.description.eligible',
  ineligible: 'slr.loan_standing.description.ineligible',
  try_again: 'slr.loan_standing.description.try_again',
  cannot_determine: 'slr.loan_standing.description.cannot_determine',
};

const REASON_TO_DESCRIPTION_KEY: Record<string, string> = {
  doc_too_old: 'slr.loan_standing.description.doc_too_old',
  nslds_date_too_old: 'slr.loan_standing.description.doc_too_old',
  insufficient_observations: 'slr.loan_standing.description.insufficient_observations',
  name_match_confirmation_required:
    'slr.loan_standing.description.name_match_confirmation_required',
  name_mismatch: 'slr.loan_standing.description.name_mismatch',
  all_parent_plus: 'slr.loan_standing.description.all_parent_plus',
  in_default: 'slr.loan_standing.description.not_in_good_standing',
  delinquency_or_collections: 'slr.loan_standing.description.not_in_good_standing',
  not_in_good_standing: 'slr.loan_standing.description.not_in_good_standing',
  nslds_required_for_federal_non_repayment_status:
    'slr.loan_standing.description.nslds_required_for_federal_non_repayment_status',
  statement_required_for_repayment_status:
    'slr.loan_standing.description.statement_required_for_repayment_status',
  unsupported_document_for_status: 'slr.loan_standing.description.unsupported_document_for_status',
  unsupported_format: 'slr.loan_standing.description.unsupported_format',
  extraction_failed: 'slr.loan_standing.description.extraction_failed',
};

const INTENT_BY_STATUS: Record<SlrLoanStandingStatus, SlrLoanStandingPresentation['intent']> = {
  eligible: 'success',
  processing: 'info',
  unknown: 'warning',
  pending_upload: 'warning',
  ineligible: 'warning',
  try_again: 'warning',
  cannot_determine: 'warning',
};

export function presentationForSlrLoanStanding(
  state: SlrLoanStandingAuditState
): SlrLoanStandingPresentation {
  const primaryReason = state.primaryReason ?? state.reasons?.[0] ?? state.failures?.[0]?.code;

  return {
    titleKey: STATUS_TO_TITLE_KEY[state.status],
    descriptionKey:
      (primaryReason && REASON_TO_DESCRIPTION_KEY[primaryReason]) ||
      STATUS_TO_DESCRIPTION_KEY[state.status],
    ctaKey: ACTION_TO_CTA_KEY[state.action],
    intent: INTENT_BY_STATUS[state.status],
    requiresAttention:
      state.action !== 'none' && state.status !== 'eligible' && state.status !== 'processing',
  };
}

export function shouldShowSlrLoanStandingAlert(state?: SlrLoanStandingAuditState | null) {
  if (!state?.featureEnabled) return false;
  const presentation = presentationForSlrLoanStanding(state);
  return presentation.requiresAttention || state.status === 'processing';
}
